import { useParams, Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, XCircle } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useBackupOperation, useCancelOperation } from "@/hooks/use-backup";
import { useIsPlatformAdmin } from "@/lib/auth";
import { Button, Card, CardContent, CardHeader, CardTitle, cn, Skeleton } from "@fox1ck-org/ui";

const STATUS_STEPS = ["pending", "running", "completed"] as const;

function StatusTimeline({ status }: { status: string }) {
  const activeIndex = STATUS_STEPS.indexOf(status as (typeof STATUS_STEPS)[number]);
  const isFailed = status === "failed";
  const isCancelled = status === "cancelled";

  return (
    <div className="flex items-center gap-2">
      {STATUS_STEPS.map((step, i) => {
        let color = "bg-muted";
        if (isFailed && i <= 1) {
          color = "bg-destructive";
        } else if (isCancelled) {
          color = i <= activeIndex ? "bg-muted-foreground" : "bg-muted";
        } else if (i <= activeIndex) {
          color = i === activeIndex ? "bg-primary" : "bg-green-500";
        }
        if (step === "completed" && status === "completed") color = "bg-green-500";

        return (
          <div key={step} className="flex items-center gap-2">
            {i > 0 && <div className={cn("w-8 h-0.5", i <= activeIndex ? "bg-muted-foreground" : "bg-muted")} />}
            <div className="flex flex-col items-center">
              <div className={cn("w-3 h-3 rounded-full", color)} />
              <span className="text-xs text-muted-foreground mt-1 capitalize">{step}</span>
            </div>
          </div>
        );
      })}
      {(isFailed || isCancelled) && (
        <>
          <div className="w-8 h-0.5 bg-muted" />
          <div className="flex flex-col items-center">
            <div className={cn("w-3 h-3 rounded-full", isFailed ? "bg-destructive" : "bg-muted-foreground")} />
            <span className="text-xs text-muted-foreground mt-1 capitalize">{status}</span>
          </div>
        </>
      )}
    </div>
  );
}

function formatTimestamp(iso?: string | null) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

function duration(start?: string | null, end?: string | null) {
  if (!start) return "-";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const diff = e - s;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s`;
  return `${Math.floor(diff / 60_000)}m ${Math.floor((diff % 60_000) / 1000)}s`;
}

const TYPE_LABELS: Record<string, string> = {
  backup: "Копия по запросу",
  restore: "Восстановление PITR",
  prod_to_dev: "Из прода в дев",
};

export default function BackupOperation() {
  const { id } = useParams<{ id: string }>();
  const { data: op, isLoading, error } = useBackupOperation(id!);
  const cancelOp = useCancelOperation();
  const isPlatformAdmin = useIsPlatformAdmin();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !op) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/backups"><ArrowLeft className="h-4 w-4" /> К резервным копиям</Link>
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {error?.message ?? "Операция не найдена"}
        </div>
      </div>
    );
  }

  const canCancel = (op.status === "pending" || op.status === "running") && isPlatformAdmin;
  const isTerminal = op.status === "completed" || op.status === "failed" || op.status === "cancelled";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/backups"><ArrowLeft className="h-5 w-5" /></Link>
          </Button>
          <div>
            <h1 className="text-[17px] font-semibold text-foreground">{TYPE_LABELS[op.operationType] ?? op.operationType}</h1>
            <p className="text-sm text-muted-foreground">{op.targetCluster} ({op.targetNamespace})</p>
          </div>
        </div>
        {canCancel && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => cancelOp.mutate(op.id, { onSuccess: () => toast.success("Операция отменена"), onError: (e) => toast.error(e.message) })}
            disabled={cancelOp.isPending}
          >
            <XCircle className="h-4 w-4" /> Отмена
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Статус</CardTitle>
        </CardHeader>
        <CardContent>
          <StatusTimeline status={op.status} />
          {!isTerminal && (
            <div className="mt-3 flex items-center gap-2 text-sm text-primary">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Обновляется автоматически…
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground">Подробности</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Detail label="ID операции" value={op.id} mono />
            <Detail label="Тип" value={TYPE_LABELS[op.operationType] ?? op.operationType} />
            <Detail label="Цель" value={`${op.targetCluster} (${op.targetNamespace})`} />
            {op.sourceCluster && <Detail label="Источник" value={`${op.sourceCluster} (${op.sourceNamespace})`} />}
            <Detail label="Автор" value={op.createdBy} />
            <Detail label="Создано" value={formatTimestamp(op.createdAt)} />
            <Detail label="Начато" value={formatTimestamp(op.startedAt)} />
            <Detail label="Завершено" value={formatTimestamp(op.completedAt)} />
            <Detail label="Длительность" value={duration(op.startedAt, op.completedAt)} />
            {op.anonymize && <Detail label="Обезличивание" value="Да" />}
            {op.recoveryTargetTime && <Detail label="Цель восстановления" value={formatTimestamp(op.recoveryTargetTime)} />}
            {op.k8sResourceName && <Detail label="Ресурс K8s" value={`${op.k8sResourceKind}/${op.k8sResourceName}`} mono />}
          </div>
        </CardContent>
      </Card>

      {op.statusMessage && (
        <div className={cn(
          "rounded-lg border p-4 text-sm",
          op.status === "failed" ? "border-destructive/50 bg-destructive/10 text-destructive" : "border bg-card text-foreground"
        )}>
          <h3 className="text-xs font-medium text-muted-foreground mb-1">Сообщение статуса</h3>
          {op.statusMessage}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs mb-0.5">{label}</p>
      <p className={cn("text-foreground", mono && "font-mono text-xs")}>{value}</p>
    </div>
  );
}
